import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../adminDataProvider';

type MuteUserParams = {
  userId: string;
  channelUrl: string;
  muted: boolean;
};

export const useMuteUser = (
  options?: UseMutationOptions<void, Error, MuteUserParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['mute-user'],
    mutationFn: ({ userId, channelUrl, muted }: MuteUserParams) =>
      dataProvider.patchMuteUser(channelUrl, userId, muted),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['muted-users'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['channel-members'],
        }),
      ]);

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: async (error, variables, onMutateResult, context) => {
      notify(translate('chat.muteUserError'), {
        autoHideDuration: 5000,
      });
      await options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};
